import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Star, ArrowRight, ShieldCheck, TrendingUp, Landmark, FileCheck, Search } from "lucide-react";
import api from "@/lib/api";
import { SITE, WHY } from "@/data/site";
import { ProjectCard } from "@/components/ProjectCard";

const HERO = "https://images.unsplash.com/photo-1769913995907-e1cc7ff527a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwcmVhbCUyMGVzdGF0ZSUyMHByb3BlcnR5JTIwbGFuZHNjYXBlfGVufDB8fHx8MTc4MjUyNzc3M3ww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { n: "500+", l: "Happy Customers" },
  { n: "20+", l: "Projects Delivered" },
  { n: "10+", l: "Years Experience" },
  { n: "100%", l: "Legal Documentation" },
];

const REVIEWS = [
  { name: "Ravi Teja", loc: "Hyderabad", text: "Booked a plot in Green Valley. Completely transparent process and clear documents. Highly recommend Next Gen!" },
  { name: "Sunitha Reddy", loc: "Secunderabad", text: "The team helped me with a bank loan and site visit pickup. Very professional and trustworthy." },
  { name: "Mahesh Kumar", loc: "Warangal", text: "Best investment decision. HMDA approved plot with great appreciation already. Thank you team!" },
];

export default function Home() {
  const nav = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [offers, setOffers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/projects", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/offers", { params: { active: true } }).then((r) => setOffers(r.data));
  }, []);

  return (
    <div className="font-body">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center">
        <img src={HERO} alt="Premium open plots" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#08341f]/90 via-[#08341f]/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full">
          <div className="max-w-2xl text-white animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black px-4 py-1.5 text-xs font-semibold tracking-wide mb-6">
              <ShieldCheck size={14} /> HMDA · DTCP · FCDA APPROVED
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Telangana's Trusted <span className="text-[#D4AF37]">Premium Open Plots</span>
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl">
              HMDA | DTCP | FCDA Approved Premium Open Plots in Prime Locations. Clear titles, bank loan assistance and the best returns on your investment.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="hero-explore" onClick={() => nav("/projects")} className="rounded-full bg-[#0B5D3B] hover:bg-[#094e32] px-7 py-3.5 font-semibold transition-colors flex items-center gap-2">
                Explore Projects <ArrowRight size={18} />
              </button>
              <button data-testid="hero-book-visit" onClick={() => nav("/book-visit")} className="rounded-full bg-white text-[#0B5D3B] hover:bg-white/90 px-7 py-3.5 font-semibold transition-colors">
                Book Free Site Visit
              </button>
              <a data-testid="hero-whatsapp" href={SITE.whatsapp()} target="_blank" rel="noopener noreferrer" className="rounded-full border-2 border-white/40 hover:bg-white/10 px-7 py-3.5 font-semibold transition-colors">
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="relative -mt-12 z-10 max-w-5xl mx-auto px-5">
        <div className="bg-white rounded-2xl shadow-[0_16px_48px_rgba(11,93,59,0.14)] p-5 md:p-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1 flex items-center gap-3 px-4 rounded-xl bg-gray-50 border border-gray-100">
            <Search size={20} className="text-[#0B5D3B]" />
            <input data-testid="home-search-input" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&nav(`/projects?q=${q}`)} placeholder="Search by location or project name…" className="w-full py-3.5 bg-transparent outline-none text-sm" />
          </div>
          <button data-testid="home-search-btn" onClick={()=>nav(`/projects?q=${q}`)} className="rounded-xl bg-[#0B5D3B] text-white px-8 py-3.5 font-semibold hover:bg-[#094e32] transition-colors">Search Plots</button>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Why Next Gen Properties</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mt-3">Built on Trust, Growth & Legal Clarity</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((w, i) => (
            <div key={w.t} style={{animationDelay:`${i*70}ms`}} className="animate-fade-up rounded-xl border border-gray-100 p-6 hover:border-[#0B5D3B]/30 hover:shadow-[0_12px_32px_rgba(11,93,59,0.08)] transition-all">
              <CheckCircle2 className="text-[#0B5D3B]" size={26} />
              <h3 className="font-heading font-semibold text-gray-900 mt-4">{w.t}</h3>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-[#f8faf9] py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Featured Projects</p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mt-3">Handpicked Premium Layouts</h2>
            </div>
            <button data-testid="view-all-projects" onClick={()=>nav("/projects")} className="hidden md:flex items-center gap-2 font-semibold text-[#0B5D3B]">View All <ArrowRight size={18}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {featured.map((p, i) => <ProjectCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Latest Offers</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mt-3">Limited Time Deals</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {offers.map((o, i) => (
            <div key={o.id} style={{animationDelay:`${i*80}ms`}} className="animate-fade-up relative rounded-2xl bg-[#0B5D3B] text-white p-7 overflow-hidden">
              <span className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#D4AF37]/20" />
              <span className="inline-block rounded-full bg-[#D4AF37] text-black px-3 py-1 text-xs font-semibold mb-4">{o.badge}</span>
              <h3 className="font-heading font-semibold text-xl">{o.title}</h3>
              <p className="text-sm text-white/80 mt-2 leading-relaxed">{o.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#08341f] py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
          {STATS.map((s) => (
            <div key={s.l}>
              <p className="font-heading text-4xl lg:text-5xl font-bold text-[#D4AF37]">{s.n}</p>
              <p className="text-sm text-white/70 mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm uppercase tracking-[0.25em] text-[#D4AF37] font-semibold">Customer Reviews</p>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-gray-900 mt-3">Loved by 500+ Families</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {REVIEWS.map((r) => (
            <div key={r.name} className="rounded-xl border border-gray-100 p-7 shadow-sm">
              <div className="flex gap-0.5 text-[#D4AF37] mb-4">{[...Array(5)].map((_,i)=><Star key={i} size={16} fill="currentColor"/>)}</div>
              <p className="text-gray-700 leading-relaxed">"{r.text}"</p>
              <div className="mt-5 flex items-center gap-3">
                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0B5D3B] text-white font-semibold">{r.name[0]}</span>
                <div><p className="font-semibold text-gray-900 text-sm">{r.name}</p><p className="text-xs text-gray-500">{r.loc}</p></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-4">
        <div className="rounded-3xl bg-gradient-to-r from-[#0B5D3B] to-[#094e32] p-10 md:p-16 text-center text-white relative overflow-hidden">
          <TrendingUp className="absolute right-10 top-10 text-white/10" size={120} />
          <h2 className="font-heading text-3xl lg:text-4xl font-bold">Ready to Own Your Dream Plot?</h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">Book a free site visit today. We'll even arrange your pickup and drop.</p>
          <button data-testid="cta-book-visit" onClick={()=>nav("/book-visit")} className="mt-8 rounded-full bg-[#D4AF37] text-black px-8 py-3.5 font-semibold hover:bg-[#c9a431] transition-colors">Book Free Site Visit</button>
        </div>
      </section>
    </div>
  );
}
